import { useQueryClient } from '@tanstack/react-query';
import { HistoryIcon } from 'lucide-react';
import { useMemo, useState, type JSX, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import type { UndoableEdit } from '@/features/file-assistant/file-edit.js';
import { UndoAssistantEditButton } from '@/features/file-assistant/UndoAssistantEditButton.js';
import {
  fileAssistantHelpHref,
  OLDER_REVISION_TEXT,
  useFileAssistant,
  type FileAssistant,
} from '@/features/file-assistant/useFileAssistant.js';
import { notify } from '@/lib/notify.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

import { ArtifactActionsMenu } from './ArtifactActionsMenu.js';
import {
  formatBytes,
  isLandscapePreviewKind,
  hasFileAssistant,
  resolvePreviewKind,
} from './artifact-display.js';
import { saveBlobAs } from './artifact-download.js';
import { FileContentBody, type PreviewFile } from './ArtifactPreview.js';
import {
  externalFileKeys,
  externalFileReference,
  externalLocationKey,
  loadExternalVersion,
  useLiveExternalEtag,
  useStableLocation,
  type ExternalFileLocation,
} from './external-file-location.js';
import { ExternalVersionHistory } from './ExternalVersionHistory.js';
import { FileViewerShell, useViewerFullScreen } from './FileViewerShell.js';
import { FullScreenToggle } from './FullScreenToggle.js';

/** Where an external file lives: what its badge says and what "Open in …" names. */
export type ExternalFileSource = 'SharePoint' | 'OneDrive';

/** Download the file's bytes through its loader, saying so on failure rather than failing silently. */
function downloadFile(file: PreviewFile): void {
  void file
    .loadBytes()
    .then((blob) => saveBlobAs(blob, file.name))
    .catch((error: unknown) =>
      notify.error(
        error instanceof Error ? error.message : 'Failed to download file',
      ),
    );
}

/**
 * The bytes on screen: an older version when one is picked, otherwise the current file keyed by its
 * eTag once the first read answers. A key that came with a list row is replaced then, so a stale row
 * is never shown, and every later change of the eTag downloads the file again.
 */
function useShownFile(
  workspaceId: string,
  file: PreviewFile,
  location: ExternalFileLocation | undefined,
  etag: string | null,
  versionId: string | null,
): PreviewFile {
  return useMemo(() => {
    if (!location) return file;
    if (versionId !== null) {
      return {
        ...file,
        size: -1,
        cacheKey: `${externalLocationKey(location)}:version:${versionId}`,
        loadBytes: () => loadExternalVersion(workspaceId, location, versionId),
      };
    }
    if (etag === null) return file;
    return {
      ...file,
      size: -1,
      cacheKey: `${externalLocationKey(location)}:${etag}`,
    };
  }, [workspaceId, file, location, etag, versionId]);
}

/** The Assistant of a file that has a location, or of none: a file without one gets no Assistant. */
function useExternalAssistant(
  workspaceId: string,
  file: PreviewFile,
  location: ExternalFileLocation | undefined,
  offersAssistant: boolean,
  viewingOlderVersion: boolean,
): FileAssistant {
  const reference = useMemo(
    () =>
      location
        ? externalFileReference(location)
        : { source: 'artifact' as const, artifactId: '' },
    [location],
  );
  return useFileAssistant({
    workspaceId,
    file: reference,
    fileName: file.name,
    enabled: offersAssistant && location !== undefined,
    composerBlocked: viewingOlderVersion ? OLDER_REVISION_TEXT : null,
  });
}

/**
 * A file from SharePoint or OneDrive, opened from the Artifacts section in the same viewer a workspace
 * artifact gets: the same {@link FileViewerShell} — one title line with the size when the source knows it,
 * the toolbar of badge, actions menu and full-screen toggle, docked beside the close button in full screen —
 * and the same {@link FileContentBody}, so every format previews exactly as it does for an artifact.
 *
 * The badge in the visibility slot names the source, because the file's visibility belongs to SharePoint or
 * OneDrive and is not ours to describe. The menu is the artifact menu given only what exists for an external
 * file: add to chat, copy our link, download, open it where it lives, and put it on the knowledge shelf.
 * Uploads, sharing, renaming, archiving and deleting belong to the source and are left out.
 *
 * Given its `location`, a file with a preview also gets the Assistant beside it, a compact Version history
 * read from the source, and Undo for the Assistant's newest edit. While it is open its eTag is read every
 * few seconds, and a change downloads the file again.
 */
export function ExternalFileViewer({
  workspaceId,
  file,
  source,
  webUrl,
  location: givenLocation,
  canWrite = false,
  onAddToChat,
  onCopyLink,
  onAddToKnowledge,
  onClose,
}: {
  workspaceId: string;
  file: PreviewFile;
  source: ExternalFileSource;
  /** The file's own page in SharePoint or OneDrive; offered only when it is plain http(s). */
  webUrl: string | null;
  /** Where the file lives, which is what the Assistant and the version history address it by. */
  location?: ExternalFileLocation;
  /**
   * Whether the caller's connection may write to the file: a SharePoint connection consented for
   * read and write, or a OneDrive folder shared for reading and writing. Restore is left out without it.
   */
  canWrite?: boolean;
  onAddToChat?: () => void;
  onCopyLink?: () => void;
  onAddToKnowledge?: () => void;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const location = useStableLocation(givenLocation);
  const kind = resolvePreviewKind(file.contentType, file.name);
  const fullScreen = useViewerFullScreen(kind !== 'none');
  const { maximized } = fullScreen;
  const href = safeHttpUrl(webUrl ?? '');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionId, setVersionId] = useState<string | null>(null);
  const { etag, settled } = useLiveExternalEtag(workspaceId, location);
  /**
   * A file opened with no key waits for its first eTag, so the bytes are always read after the eTag
   * they are keyed by: an edit in between shows up as a new eTag on the next read and reloads them.
   */
  const waitingForEtag =
    location !== undefined && file.cacheKey === null && !settled;
  const shownFile = useShownFile(workspaceId, file, location, etag, versionId);
  const assistant = useExternalAssistant(
    workspaceId,
    file,
    location,
    hasFileAssistant(kind),
    versionId !== null,
  );
  const fill = !(kind === 'pptx' && !maximized);
  const refreshFile = (edit: UndoableEdit): void => {
    assistant.markUndone(edit);
    if (!location) return;
    void queryClient.invalidateQueries({
      queryKey: externalFileKeys.item(workspaceId, location),
    });
  };
  return (
    <FileViewerShell
      title={file.name}
      titleMeta={file.size >= 0 ? formatBytes(file.size) : undefined}
      widthClass={isLandscapePreviewKind(kind) ? 'max-w-6xl' : 'max-w-3xl'}
      fill={fill}
      maximized={maximized}
      onLeaveFullScreen={fullScreen.leave}
      onClose={onClose}
      assistant={assistant.layout}
      helpHref={assistant.available ? fileAssistantHelpHref() : undefined}
      toolbar={
        <>
          <Badge variant="outline" className="text-[10px]">
            {source}
          </Badge>
          {location ? (
            <Button
              size="sm"
              variant="ghost"
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen((open) => !open)}
            >
              <HistoryIcon className="size-4" />
              Version history
            </Button>
          ) : null}
          {assistant.undoableEdit && versionId === null ? (
            <UndoAssistantEditButton
              edit={assistant.undoableEdit}
              onUndone={refreshFile}
            />
          ) : null}
          <ArtifactActionsMenu
            artifactName={file.name}
            onAddToChat={onAddToChat}
            onCopyLink={onCopyLink}
            onDownload={() => downloadFile(shownFile)}
            openExternally={
              href === '' ? undefined : { label: `Open in ${source}`, href }
            }
            knowledgeAction={
              onAddToKnowledge
                ? { mode: 'add', onSelect: onAddToKnowledge }
                : undefined
            }
          />
          {kind !== 'none' ? (
            <FullScreenToggle
              maximized={maximized}
              onToggle={fullScreen.toggle}
            />
          ) : null}
        </>
      }
      banner={
        versionId !== null ? (
          <OlderVersionBanner onBack={() => setVersionId(null)} />
        ) : undefined
      }
      footer={
        historyOpen && location ? (
          <div data-testid="external-viewer-history" className="shrink-0">
            <ExternalVersionHistory
              workspaceId={workspaceId}
              location={location}
              etag={etag}
              canRestore={canWrite}
              selectedVersionId={versionId}
              onSelectVersion={setVersionId}
            />
          </div>
        ) : undefined
      }
    >
      {waitingForEtag ? (
        <Skeleton className="h-full min-h-40 w-full rounded-lg" />
      ) : (
        <FileContentBody
          workspaceId={workspaceId}
          file={shownFile}
          fill={fill}
        />
      )}
    </FileViewerShell>
  );
}

/** Says an older version is on screen, with the way back to the current one. */
function OlderVersionBanner({ onBack }: { onBack: () => void }): ReactNode {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
      <p role="status" className="text-sm text-muted-foreground">
        Showing an older version of this file.
      </p>
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onBack}>
        Back to latest
      </Button>
    </div>
  );
}
